import { useId, useRef, useState, type ReactNode } from 'react';

export interface WorkflowStep {
  title: string;
  status: string;
  complete: boolean;
  content: ReactNode;
}

/** One process, one detail pane. Completed steps remain inspectable, not duplicated. */
export function WorkflowStepper({ label, steps, currentIndex, needsAttention = false }: {
  label: string; steps: readonly WorkflowStep[]; currentIndex: number; needsAttention?: boolean;
}) {
  const id = useId();
  const stepButtons = useRef<Array<HTMLButtonElement | null>>([]);
  const [inspection, setInspection] = useState<{ current: number; selected: number } | null>(null);
  const selected = inspection?.current === currentIndex ? inspection.selected : currentIndex;
  const step = steps[selected];
  return <div className="workflow-stepper">
    <nav aria-label={label}>
      <ol className="workflow-stepper__steps">
        {steps.map((item, index) => <li key={item.title} className={`${item.complete ? 'is-complete' : index === currentIndex ? needsAttention ? 'needs-attention' : 'is-current' : 'is-upcoming'}`}>
          <button className="workflow-stepper__step" type="button" id={`${id}-step-${index}`} ref={node => { stepButtons.current[index] = node; }} disabled={!item.complete && index !== currentIndex}
            aria-current={index === currentIndex ? 'step' : undefined} aria-expanded={selected === index} aria-controls={`${id}-detail`}
            onClick={() => setInspection({ current: currentIndex, selected: index })}>
            <span className="workflow-stepper__marker" aria-hidden="true">{item.complete ? '✓' : index + 1}</span>
            <span className="workflow-stepper__label"><strong>{item.title}</strong><small>{item.complete ? 'Complete' : item.status}</small></span>
          </button>
        </li>)}
      </ol>
    </nav>
    {step && <section className="workflow-stepper__detail" id={`${id}-detail`} aria-labelledby={`${id}-step-${selected}`}>
      <span className="eyebrow">{selected === currentIndex ? `Current step · ${selected + 1} of ${steps.length}` : `Completed step · ${selected + 1} of ${steps.length}`}</span>
      <h4>{step.title}</h4>
      {step.content}
      {selected !== currentIndex && <button className="button button--secondary" type="button" onClick={() => { setInspection(null); stepButtons.current[currentIndex]?.focus(); }}>Return to current step</button>}
    </section>}
  </div>;
}
